import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lock, Play, PlayCircle, Tv, Headphones, Calculator,
  ArrowUp, Loader2, Mail, User, ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { getAttributionPayload, getListOverride, captureUtmFromUrl } from "@/lib/utm";

/**
 * VOD Squeeze Page — /vod
 * Dedicated Funnel B entry: lead capture (name + email only, no password),
 * dark stone-950 + amber theme matching the community area. On submit → shows
 * success screen instructing the user to check email for the magic link.
 *
 * Sticky Attribution: the URL's `?list=X` param is captured, forwarded to the
 * backend, and used to attribute the lead to an affiliate. If the same email
 * signs up again with a different list, First Click Wins is enforced server-side.
 */
export default function VODSignup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null); // brand/hero text from /vod/campaign
  const [form, setForm] = useState({ name: "", email: "", marketing_consent: true });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  // Capture ?list=X + UTMs the moment the page mounts
  useEffect(() => {
    captureUtmFromUrl();
    (async () => {
      try {
        const { data } = await api.get("/vod/campaign");
        setMeta(data);
      } catch (err) {
        // Fallback copy if endpoint hiccups — page must still render
        setMeta({
          brand_name: "אמת או חובות",
          hero_title: "תפסיקו לממן את הבנקים. תתחילו לממן את העתיד שלכם.",
          hero_subtitle: "פלטפורמת VOD מתקדמת שתלמד אתכם — צעד אחר צעד — איך לפרק את החובות היקרים, לייצר חמצן בתזרים החודשי, ולחזור לישון בשקט. בלי סיסמאות — רק הנדסה פיננסית פרקטית.",
          cta_primary: "צור חשבון והתחל לצפות עכשיו",
          marketing_consent_text: "אני מאשר/ת קבלת עדכונים וכלים פיננסיים במייל ובווטסאפ.",
        });
      }
    })();
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "איך לקרוא לך?";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = "אימייל לא תקין";
    }
    if (!form.marketing_consent) e.marketing_consent = "יש לאשר קבלת עדכונים כדי להשלים הרשמה";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const attribution = getAttributionPayload();
      const listId = params.get("list") || getListOverride() || null;
      const { data } = await api.post("/vod/signup", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        list_id: listId,
        marketing_consent: form.marketing_consent,
        ...attribution,
      });
      toast.success(data.message || "ברוכים הבאים!");
      // AUTO-LOGIN: server set the member_session cookie; go straight into the community.
      // Pass a signal via location.state so the Community page can show a welcome banner
      // with the magic-link explanation right after signup.
      navigate("/community", {
        replace: true,
        state: { justSignedUp: true, magicLinkUrl: data.magic_link_url || null },
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "אירעה שגיאה. נסה/י שוב.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // ===== Main squeeze page =====
  const brand = meta?.brand_name || "אמת או חובות";

  return (
    <div dir="rtl" className="min-h-screen bg-stone-950 text-stone-100 relative overflow-x-hidden" data-testid="vod-signup-page">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-amber-600/8 blur-3xl" />
        <div className="absolute bottom-40 left-0 w-[500px] h-[500px] rounded-full bg-amber-500/5 blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-stone-900">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-[Rubik] font-black text-xl text-stone-50 hover:text-amber-500 transition-colors" data-testid="vod-brand-link">
            {brand}
          </Link>
          <Link to="/magic-login" className="text-sm text-stone-400 hover:text-amber-500 flex items-center gap-1" data-testid="vod-already-member">
            כבר חבר/ה? כניסה
            <ChevronLeft className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* HERO + FORM */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-10 md:pt-16 pb-20">
        {/* Pre-title */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-2 text-amber-500 text-xs sm:text-sm font-bold tracking-widest uppercase mb-6"
          data-testid="vod-hero-pretitle"
        >
          <Lock className="w-4 h-4" />
          אזור סגור לחברי הקהילה בלבד
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="font-[Rubik] font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-stone-50 mb-6" data-testid="vod-hero-h1">
              {meta?.hero_title || "תפסיקו לממן את הבנקים. תתחילו לממן את העתיד שלכם."}
            </h1>
            <p className="text-lg text-stone-300 leading-relaxed mb-6" data-testid="vod-hero-h2">
              {meta?.hero_subtitle || ""}
            </p>
            <div className="flex items-center gap-2 text-sm text-stone-400" data-testid="vod-hero-social-proof">
              <span className="text-amber-500 text-base">⭐</span>
              <span>הצטרפו ל-<b className="text-stone-100">500+ משפחות</b> שכבר עשו את השינוי. ההרשמה בחינם.</span>
            </div>
          </motion.div>

          {/* Registration form */}
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="bg-stone-900/80 backdrop-blur-md border border-stone-800 rounded-3xl p-6 sm:p-8 shadow-2xl shadow-black/40"
            data-testid="vod-form-card"
          >
            <div className="text-center mb-6">
              <h2 className="font-[Rubik] font-bold text-2xl text-stone-50 mb-1">יצירת משתמש באזור האישי</h2>
              <p className="text-sm text-stone-400">ההרשמה חינם ולוקחת פחות מדקה</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="vod-signup-form">
              <div>
                <Label htmlFor="vod-name" className="text-stone-300 text-sm mb-1.5 block">שם פרטי</Label>
                <div className="relative">
                  <User className="w-4 h-4 text-stone-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    id="vod-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="איך לקרוא לכם?"
                    className="h-12 bg-stone-950 border-stone-800 text-stone-100 placeholder:text-stone-600 pr-10 focus-visible:border-amber-600"
                    data-testid="vod-input-name"
                    aria-invalid={!!errors.name}
                  />
                </div>
                {errors.name && <div className="text-xs text-red-400 mt-1" data-testid="vod-error-name">{errors.name}</div>}
              </div>

              <div>
                <Label htmlFor="vod-email" className="text-stone-300 text-sm mb-1.5 block">אימייל</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-stone-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    id="vod-email"
                    type="email"
                    dir="ltr"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="the.address@example.com"
                    className="h-12 bg-stone-950 border-stone-800 text-stone-100 placeholder:text-stone-600 pr-10 focus-visible:border-amber-600 text-right"
                    data-testid="vod-input-email"
                    aria-invalid={!!errors.email}
                  />
                </div>
                <div className="text-[11px] text-stone-500 mt-1">הכתובת אליה נשלח את הגישה</div>
                {errors.email && <div className="text-xs text-red-400 mt-1" data-testid="vod-error-email">{errors.email}</div>}
              </div>

              <div className="flex items-start gap-2.5 pt-2">
                <Checkbox
                  id="vod-consent"
                  checked={form.marketing_consent}
                  onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })}
                  className="mt-0.5 border-stone-700 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                  data-testid="vod-consent-checkbox"
                />
                <Label htmlFor="vod-consent" className="text-xs text-stone-400 leading-relaxed cursor-pointer">
                  {meta?.marketing_consent_text || "אני מאשר/ת קבלת עדכונים וכלים פיננסיים במייל ובווטסאפ."}
                  {" "}
                  קראתי ואישרתי את{" "}
                  <Link to="/privacy" target="_blank" className="text-amber-500 hover:text-amber-400 underline">
                    מדיניות הפרטיות
                  </Link>
                  {". "}
                  <span className="text-red-400">(חובה)</span>
                </Label>
              </div>
              {errors.marketing_consent && <div className="text-xs text-red-400" data-testid="vod-error-consent">{errors.marketing_consent}</div>}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-14 bg-amber-600 hover:bg-amber-500 text-stone-950 font-bold text-lg rounded-full transition-all active:scale-[0.98] disabled:opacity-70"
                data-testid="vod-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin me-2" />שולח לינק כניסה…</>
                ) : (
                  <><Play className="w-5 h-5 me-2 fill-current" />{meta?.cta_primary || "צור חשבון והתחל לצפות עכשיו"}</>
                )}
              </Button>

              <p className="text-[11px] text-stone-500 text-center leading-relaxed pt-1">
                ההרשמה פתוחה חינם לזמן מוגבל מתוך מטרה לחזק את הקהילה.
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16 border-t border-stone-900" data-testid="vod-benefits-section">
        <div className="text-center mb-12">
          <div className="text-xs font-bold text-amber-500 tracking-widest uppercase mb-3">מה מחכה בפנים?</div>
          <h2 className="font-[Rubik] font-black text-3xl sm:text-4xl text-stone-50">שלושה כלים שיחזירו לכם את השליטה</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <BenefitCard
            testid="benefit-vod"
            Icon={Tv}
            title="וידאו פרקטי, ישר ולעניין"
            text='ספריית VOD ממוקדת המחולקת לקטגוריות: מיינדסט, פירוק חוסמים, איחוד הלוואות וצמיחה. סרטונים קצרים שמסבירים בדיוק מה עובד היום בישראל.'
            tag="ספריית VOD"
          />
          <BenefitCard
            testid="benefit-pip"
            Icon={Headphones}
            title="איתכם גם כשאתם עסוקים"
            text='אין לכם זמן לשבת מול המסך? הנגן החכם שלנו מאפשר להמשיך לצפות בחלון צף קטן (PiP) בזמן שאתם שוטפים כלים, נוהגים או עובדים באפליקציות אחרות.'
            tag='Picture-in-Picture'
          />
          <BenefitCard
            testid="benefit-calc"
            Icon={Calculator}
            title="הכלים שלנו בידיים שלכם"
            text='מעבר לסרטונים, האזור האישי יכלול את "מחשבון המיפוי" ו"סימולטור החמצן" שלנו. תשכחו מאקסלים מסובכים — המערכת תחשב עבורכם הכל בלחיצת כפתור.'
            tag="מחשבונים בלעדיים"
          />
        </div>
      </section>

      {/* SNEAK PEEK */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16 border-t border-stone-900" data-testid="vod-sneak-peek">
        <div className="text-center mb-10">
          <div className="text-xs font-bold text-amber-500 tracking-widest uppercase mb-3">הצצה פנימה</div>
          <h2 className="font-[Rubik] font-black text-3xl sm:text-4xl text-stone-50">טעימה ממה שתראו בפנים…</h2>
        </div>

        {/* Mockup: netflix-style VOD carousel */}
        <div className="bg-gradient-to-b from-stone-900 to-stone-950 border border-stone-800 rounded-3xl p-6 sm:p-10 shadow-2xl shadow-black/60">
          {/* Fake browser chrome */}
          <div className="flex items-center gap-1.5 mb-6">
            <div className="w-3 h-3 rounded-full bg-red-500/60" />
            <div className="w-3 h-3 rounded-full bg-amber-500/60" />
            <div className="w-3 h-3 rounded-full bg-green-500/60" />
            <div className="mr-3 text-[11px] text-stone-500 font-mono">community.emet-o-chovot.co.il/community</div>
          </div>

          {/* Sample swimlane */}
          <MockLane
            title="מיינדסט: איך מפסיקים לריב על כסף בבית"
            videos={[
              { title: "השיחה שהצילה את התא", duration: "07:20" },
              { title: '5 האמונות שהופכות אתכם ל"עבדים"', duration: "12:04" },
              { title: "איך לדבר על כסף עם בן/בת הזוג", duration: "09:15" },
            ]}
          />
          <MockLane
            title="טקטיקה: לעצור את דימום הריביות ב-30 יום"
            videos={[
              { title: "מיפוי החובות בזמן אמת", duration: "08:42" },
              { title: "פרק החובות ה-3 שרובכם משלמים מיותר", duration: "11:33" },
              { title: "הטלפון של 5 הדקות שיוריד לכם ₪400 בחודש", duration: "05:55" },
            ]}
          />
          <MockLane
            title="סודות המשכנתה: איחוד הלוואות חכם"
            last
            videos={[
              { title: "מתי איחוד הלוואות זו טעות", duration: "10:12" },
              { title: '"מסלול צמוד" מול "משתנה" — מה נכון היום', duration: "14:20" },
              { title: "המסמכים ש-95% מגישים לא נכון", duration: "06:48" },
            ]}
          />
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-20 text-center" data-testid="vod-bottom-cta">
        <motion.h2
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-[Rubik] font-black text-3xl sm:text-4xl lg:text-5xl text-stone-50 mb-4 leading-tight"
        >
          הפסיקו לדחות את זה למחר.
          <br />
          <span className="text-amber-500">החופש הכלכלי שלכם מתחיל בלחיצת כפתור.</span>
        </motion.h2>
        <p className="text-stone-400 text-lg mb-8 max-w-xl mx-auto">
          כמה שעות של צפייה — ואתם כבר לא באותו מקום.
        </p>
        <Button
          onClick={scrollToForm}
          className="h-14 px-10 bg-amber-600 hover:bg-amber-500 text-stone-950 font-bold text-lg rounded-full active:scale-95"
          data-testid="vod-scroll-to-form"
        >
          <Lock className="w-5 h-5 me-2" />
          לכניסה מאובטחת ויצירת חשבון בחינם
          <ArrowUp className="w-4 h-4 me-3 opacity-70" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-stone-900 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-3 text-xs text-stone-500">
          <div>© {new Date().getFullYear()} {brand}. כל הזכויות שמורות.</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-amber-500 transition-colors">מדיניות פרטיות</Link>
            <Link to="/terms" className="hover:text-amber-500 transition-colors">תנאי שימוש</Link>
            <Link to="/contact" className="hover:text-amber-500 transition-colors">צור קשר</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function BenefitCard({ Icon, title, text, tag, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5 }}
      className="bg-stone-900/60 backdrop-blur-sm border border-stone-800 hover:border-amber-600/40 rounded-2xl p-6 transition-colors group"
      data-testid={testid}
    >
      <div className="w-12 h-12 rounded-2xl bg-amber-600/15 border border-amber-600/30 flex items-center justify-center mb-4 group-hover:bg-amber-600/25 transition-colors">
        <Icon className="w-6 h-6 text-amber-500" />
      </div>
      <div className="text-[10px] text-amber-500 font-bold tracking-widest uppercase mb-1.5">{tag}</div>
      <h3 className="font-[Rubik] font-bold text-lg text-stone-50 mb-2 leading-snug">{title}</h3>
      <p className="text-sm text-stone-400 leading-relaxed">{text}</p>
    </motion.div>
  );
}

function MockLane({ title, videos, last }) {
  return (
    <div className={last ? "" : "mb-6"}>
      <div className="text-sm font-bold text-stone-200 mb-2.5 flex items-center gap-2">
        <span className="w-1 h-4 bg-amber-500 rounded-full" />
        {title}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x scrollbar-hide">
        {videos.map((v, i) => (
          <div
            key={i}
            className="min-w-[180px] w-[180px] snap-start bg-stone-900 border border-stone-800 rounded-lg overflow-hidden shrink-0 hover:border-amber-600/40 transition-colors group"
          >
            <div className="aspect-video bg-gradient-to-br from-stone-800 to-stone-950 flex items-center justify-center relative">
              <PlayCircle className="w-10 h-10 text-amber-500/70 group-hover:text-amber-500 transition-colors" />
              <div className="absolute bottom-1.5 right-1.5 bg-black/70 text-[10px] px-1.5 py-0.5 rounded text-stone-200 font-mono">
                {v.duration}
              </div>
            </div>
            <div className="p-2.5">
              <div className="text-xs text-stone-200 font-medium line-clamp-2 leading-snug">{v.title}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
